import React, { useCallback } from "react";
import { useFormikContext } from "formik";
import { Grid } from "@material-ui/core";
import { InputDateRange } from "components/DateRangePicker/dateRangePicker";
import * as Yup from "yup";
import { SelectField, withNamespace } from "components/Form/FormikControls";

export interface FinancialStatementFormValues {
  startDate: Date | null;
  endDate: Date | null;
}

type FinancialStatementFormProps = {
  namespace?: string;
};

export function FinancialStatementForm(
  props: FinancialStatementFormProps,
): React.JSX.Element {
  const { namespace: ns = "" } = props;

  const { values: formValues, setValues } =
    useFormikContext<FinancialStatementFormValues>();

  const updateDates = useCallback(
    (startDate, endDate) => {
      const newValue: FinancialStatementFormValues = {
        ...formValues,
        // Naively apply the new dates to the root of the form values
        startDate,
        endDate,
      };
      setValues(newValue, true);
    },
    [formValues, setValues],
  );

  return (
    <>
      <Grid item xs={12}>
        <p>
          Generate Financial Statements containing start balance, closing
          balance, charges and payments within the given date range.
        </p>
      </Grid>

      <InputDateRange
        endDateFieldName={withNamespace(ns, "endDate")}
        endDateLabel="End date"
        includeTime={false}
        showRangeOptions
        showYearOptions
        startDateFieldName={withNamespace(ns, "startDate")}
        startDateLabel="Start date"
        updateDates={updateDates}
      />
    </>
  );
}

export enum ExcludeZeroBalance {
  END_DATE = "END_DATE",
  CURRENT = "CURRENT",
}

const excludeZeroBalanceOptions = [
  { label: "Balance at end date is zero", value: ExcludeZeroBalance.END_DATE },
  { label: "Current balance is zero", value: ExcludeZeroBalance.CURRENT },
];

export interface ExcludeZeroBalanceFormValues {
  excludeZeroBalance: ExcludeZeroBalance | null;
}

export const excludeZeroBalanceValidation = Yup.mixed<ExcludeZeroBalance>()
  .oneOf([...Object.values(ExcludeZeroBalance), null])
  .nullable()
  .test(
    "end-date-required",
    "Choose an end date to skip on the balance at end date",
    function endDateRequired(value) {
      return value !== ExcludeZeroBalance.END_DATE || !!this.parent.endDate;
    },
  );

export function ExcludeZeroBalanceSelect(): React.JSX.Element {
  return (
    <Grid item xs={12}>
      <SelectField
        isClearable
        label="Skip statement when"
        menuPortalTarget={document.body}
        name="excludeZeroBalance"
        options={excludeZeroBalanceOptions}
        placeholder="Never"
      />
    </Grid>
  );
}
